import { useParams, useRouteMatch } from "react-router-dom";
import { useEffect } from "react";
import PartsAIChat from "./PartsAIChat";
import { type DialogueKind, usePartsAIStore } from "./store/PartsAIStore";
import AtomAssistPageLoad from "src/views/assistant/components/AtomAssistPageLoad";
import { useChatStore } from "src/views/assistant/stores/ChatStore";

interface PartsAIChatPageParams {
  parts_ai_run_id?: string;
  dialogueId: string;
}

const PartsAIChatPage = () => {
  const { parts_ai_run_id: runIdParam, dialogueId: dialogueIdParam } =
    useParams<PartsAIChatPageParams>();

  // Route-discriminator — `/parts/chat/search/:dialogueId` has no runId in
  // the path. We detect it from the URL rather than from the route name so
  // there's only one place to look when adding future dialogue kinds.
  const match = useRouteMatch();
  // Comparison dialogues route to the Item Equivalency chat page now, so
  // only search and run reach this one.
  const dialogueKind: DialogueKind = match.path.startsWith(
    "/parts/chat/search/",
  )
    ? "search"
    : "run";

  const partsAIRunId = runIdParam ? Number(runIdParam) : undefined;
  const dialogueId = dialogueIdParam ? Number(dialogueIdParam) : undefined;

  // get initialUserMessage from chat store (set by splash page)
  const { initialUserMessage, setProductConfig } = useChatStore();

  // Sync the Parts AI run ID, dialogue ID and dialogue kind into the store
  // on every route change — always assign (even when undefined) so
  // navigating between dialogue types clears the previous run/kind instead
  // of letting consumers see stale values.
  const { setPartsAIRunId, setDialogueId, setDialogueKind, reset } =
    usePartsAIStore();
  useEffect(() => {
    setPartsAIRunId(partsAIRunId);
    setDialogueId(dialogueId);
    setDialogueKind(dialogueKind);
  }, [
    partsAIRunId,
    dialogueId,
    dialogueKind,
    setPartsAIRunId,
    setDialogueId,
    setDialogueKind,
  ]);

  // Leaving Parts AI entirely should wipe the store so a return visit
  // doesn't transiently render against a stale run/dialogue.
  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  // Set product configuration
  useEffect(() => {
    setProductConfig({
      productKey: "parts-ai",
      productName: "Parts AI",
      basePath: "/parts",
    });
  }, [setProductConfig]);

  // partsAIRunId is optional: comparison chats are linked to two parts but
  // not to a parts_ai.run, so we only block on dialogueId being present.
  if (!dialogueId) {
    return <AtomAssistPageLoad />;
  }

  return (
    <PartsAIChat
      partsAIRunId={partsAIRunId}
      dialogueId={dialogueId}
      initialUserMessage={initialUserMessage}
    />
  );
};

export default PartsAIChatPage;
